// Turns the search response into the groups the pop-up shows (FECOA-2184).
// Every item gets a stable key, a title, a short line of context and the page
// it opens. Links follow the same rules as the rest of the app (see
// notificationNavigation.js): studies and sites use their public ids.

const STUDY_STATUS = { planned: 'Planned', in_progress: 'In Progress', completed: 'Completed' };
// Review states, worded as on the Assessment Builder cards.
const REVIEW_STATUS = {
  draft: 'Draft',
  in_review: 'Pending Review',
  approved: 'Approved',
  published: 'Published',
  denied: 'Changes Requested',
  archived: 'Archived',
  superseded: 'Superseded',
};

const joinParts = (...parts) => parts.filter(Boolean).join(' · ');

const titleCase = (value) =>
  value ? String(value).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '';

const inactive = (row) => (row.is_active === false ? 'Inactive' : '');

const BUILDERS = {
  studies: (r) =>
    r.id && {
      title: r.title || r.code,
      subtitle: joinParts(r.title && r.code, STUDY_STATUS[r.status] || titleCase(r.status)),
      url: `/studies/${r.id}`,
    },
  sites: (r) =>
    r.id &&
    r.study_id && {
      title: r.name || r.code,
      subtitle: joinParts(r.name && r.code, inactive(r), r.study_title),
      url: `/studies/${r.study_id}/sites/${r.id}`,
    },
  participants: (r) =>
    r.id &&
    r.site_id &&
    r.study_id && {
      title: `Participant ${r.label}`,
      subtitle: joinParts(inactive(r), r.site_name, r.study_title),
      url: `/studies/${r.study_id}/sites/${r.site_id}/participant/${r.id}`,
    },
  // Names only arrive for people allowed to see them; otherwise the caregiver ID leads.
  caregivers: (r) =>
    r.id &&
    r.participant_id &&
    r.site_id &&
    r.study_id && {
      title: r.name || r.label,
      subtitle: joinParts(
        r.name && r.label,
        r.participant_label && `Caregiver for participant ${r.participant_label}`,
        r.study_title,
      ),
      url: `/studies/${r.study_id}/sites/${r.site_id}/participant/${r.participant_id}/?selected_caregiver=${r.id}#caregivers`,
    },
  dcqs: (r) =>
    r.id &&
    r.study_id && {
      title: joinParts(`DCQ #${r.id}`, r.summary),
      subtitle: joinParts(r.participant_label && `Participant ${r.participant_label}`, r.status, r.study_title),
      url: `/studies/${r.study_id}/dcq/${r.id}`,
    },
  assessments: (r) =>
    r.id && {
      title: r.name,
      subtitle: joinParts(r.library, REVIEW_STATUS[r.status] || titleCase(r.status)),
      url: `/assessment-builder/${r.id}`,
    },
  study_drafts: (r) =>
    r.name && {
      title: r.name,
      subtitle: joinParts('Study draft', REVIEW_STATUS[r.status] || titleCase(r.status)),
      url: `/studybuilder/${encodeURIComponent(r.name)}`,
    },
};

// Group order in the pop-up, with the names the app already uses.
export const RESULT_GROUPS = [
  { key: 'studies', type: 'study', label: 'Studies' },
  { key: 'sites', type: 'site', label: 'Sites' },
  { key: 'participants', type: 'participant', label: 'Participants' },
  { key: 'caregivers', type: 'caregiver', label: 'Caregivers' },
  { key: 'dcqs', type: 'dcq', label: 'DCQs' },
  { key: 'assessments', type: 'assessment', label: 'Assessments' },
  { key: 'study_drafts', type: 'study_draft', label: 'Study drafts' },
];

export const buildResultGroups = (data) => {
  const results = data?.results || {};
  return RESULT_GROUPS.map((group) => {
    const rows = Array.isArray(results[group.key]) ? results[group.key] : [];
    const items = rows
      .map((row) => {
        const built = BUILDERS[group.key](row);
        if (!built || !built.title || !built.url) return null;
        return { ...built, type: group.type, key: `${group.type}:${row.id ?? row.name}` };
      })
      .filter(Boolean);
    return { key: group.key, label: group.label, items };
  }).filter((group) => group.items.length > 0);
};

// Pages match on their name or a few extra words, every typed word must match.
export const matchPages = (pages, query) => {
  const words = query.toLowerCase().split(' ').filter(Boolean);
  if (!words.length) return pages;
  return pages.filter((page) => {
    const haystack = `${page.title} ${page.keywords || ''}`.toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Splits text into plain and matching parts so the pop-up can mark the match.
export const splitMatches = (text, query) => {
  const value = String(text ?? '');
  const words = query.split(' ').filter(Boolean).map(escapeRegExp);
  if (!value || !words.length) return [{ text: value, match: false }];
  const pattern = new RegExp(`(${words.join('|')})`, 'ig');
  return value
    .split(pattern)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, match: words.some((word) => new RegExp(`^${word}$`, 'i').test(part)) }));
};

// Recently opened results, kept for this browser tab only so record names do
// not stay on a shared computer after the window is closed.
const RECENT_LIMIT = 5;
const recentKey = (userKey) => `flexcoa-search-recent:${userKey || 'anonymous'}`;

export const readRecent = (userKey) => {
  try {
    const items = JSON.parse(sessionStorage.getItem(recentKey(userKey)) || '[]');
    return Array.isArray(items) ? items.filter((item) => item && item.key && item.url && item.title) : [];
  } catch {
    return [];
  }
};

export const rememberRecent = (userKey, item) => {
  const entry = { key: item.key, type: item.type, title: item.title, subtitle: item.subtitle || '', url: item.url };
  try {
    const next = [entry, ...readRecent(userKey).filter((existing) => existing.key !== entry.key)].slice(0, RECENT_LIMIT);
    sessionStorage.setItem(recentKey(userKey), JSON.stringify(next));
  } catch {
    /* storage can be unavailable (private mode); recent items are optional */
  }
};

export const clearRecent = (userKey) => {
  try {
    sessionStorage.removeItem(recentKey(userKey));
  } catch {
    /* ignore */
  }
};
